import {
  Button,
  IconButton,
  Input,
  Select,
  SidePanel,
  StatusPill,
  formatCurrency,
  useSnackbar,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { HandCoins } from 'lucide-react';
import { useState } from 'react';
import { RequestAlert } from '../../app/data-view.js';
import { commissionsApi, getApiError, type Sale, type SaleCommissionPart } from '../../lib/api.js';

const STATUS: Record<
  SaleCommissionPart['status'],
  { label: string; tone: 'warning' | 'positive' | 'neutral' }
> = {
  PENDING: { label: 'Espera o atendimento', tone: 'warning' },
  ACCRUED: { label: 'Nasceu', tone: 'positive' },
  VOIDED: { label: 'Retirada', tone: 'neutral' },
};

function Reassign({ part, onDone }: { part: SaleCommissionPart; onDone: () => Promise<void> }) {
  const snackbar = useSnackbar();
  const [resourceId, setResourceId] = useState(part.resourceId);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const receivers = useQuery({
    queryKey: ['company', 'commissions', 'receivers'],
    queryFn: commissionsApi.receivers,
    retry: false,
  });
  async function save() {
    if (reason.trim().length < 3) return setError('Diga o motivo da troca.');
    setSaving(true);
    try {
      await commissionsApi.reassignPart(part.id, {
        resourceId: resourceId || null,
        reason: reason.trim(),
      });
      snackbar.success('Comissão alterada.');
      await onDone();
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="mt-2 grid gap-2">
      {error ? <RequestAlert message={error} /> : null}
      <Select
        label={`Quem recebe ${part.lineDisplayName}`}
        value={resourceId}
        options={[
          { value: '', label: 'Sem comissão' },
          ...(receivers.data ?? []).map((one) => ({
            value: one.id,
            label: one.displayName,
          })),
        ]}
        onValueChange={setResourceId}
      />
      <Input
        label="Motivo"
        value={reason}
        maxLength={500}
        onChange={(event) => setReason(event.target.value)}
      />
      <Button type="button" disabled={saving} onClick={() => void save()}>
        Salvar troca
      </Button>
    </div>
  );
}

/// As comissões de uma venda, com o valor, e a troca com motivo (ADR 0082, decisões 7 e 13).
export function SaleCommissionsButton({ sale, canReassign }: { sale: Sale; canReassign: boolean }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const key = ['company', 'commissions', 'sale', sale.id];
  const parts = useQuery({
    queryKey: key,
    queryFn: () => commissionsApi.saleParts(sale.id),
    enabled: open,
    retry: false,
  });
  return (
    <>
      <IconButton label="Ver comissões" icon={<HandCoins />} onClick={() => setOpen(true)} />
      <SidePanel
        open={open}
        onOpenChange={setOpen}
        title={`Comissões da venda ${String(sale.number)}`}
        description="A comissão que já nasceu se mantém. Trocar ou retirar exige motivo e fica na auditoria."
      >
        {parts.isLoading ? <p role="status">Carregando comissões…</p> : null}
        {parts.error ? <RequestAlert message={getApiError(parts.error)} /> : null}
        {parts.data?.length === 0 ? (
          <p className="text-sm text-content-faint">Esta venda não tem comissão.</p>
        ) : null}
        <ul className="grid gap-3" aria-label="Comissões da venda">
          {(parts.data ?? []).map((part) => (
            <li key={part.id} className="rounded-xl border border-border p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold">
                  {part.lineDisplayName}
                  {part.quantity > 1 ? ` · ${String(part.quantity)} unidades` : ''}
                </p>
                <StatusPill tone={STATUS[part.status].tone}>{STATUS[part.status].label}</StatusPill>
              </div>
              <p>
                {part.resourceDisplayName}
                {part.role === 'ASSISTANT' ? ' (assistente)' : ''}:{' '}
                {formatCurrency(part.amountCents, 'BRL')}
              </p>
              {part.closed ? (
                <p className="text-xs text-content-faint">Já apurada: não muda mais.</p>
              ) : null}
              {canReassign && !part.closed && part.status !== 'VOIDED' ? (
                editing === part.id ? (
                  <Reassign
                    part={part}
                    onDone={async () => {
                      setEditing(null);
                      await queryClient.invalidateQueries({ queryKey: key });
                    }}
                  />
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-2"
                    onClick={() => setEditing(part.id)}
                  >
                    Trocar ou retirar
                  </Button>
                )
              ) : null}
            </li>
          ))}
        </ul>
      </SidePanel>
    </>
  );
}
